import {
  bulkDeletePostsResponseSchema,
  deletePostResponseSchema,
  openPostResponseSchema,
  postDetailResponseSchema,
  postListResponseSchema,
  postReviewResponseSchema,
  type BulkDeletePostsResponse,
  type OpenPostResponse,
  type PaginationMeta,
  type PostDetailResponse,
  type PostIngested,
  type PostIntent,
  type PostListItem,
  type PostReviewRequest,
  type PostReviewStatus,
  type PostSortField,
  type PostSourceType,
  type SortOrder,
} from '@midiautolistening/contracts';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost, toApiError } from '@/lib/api';
import type { PostMatchId } from '@/lib/post-identity';

/**
 * Posts: the filtered list, the detail drawer and the review actions.
 *
 * Scale discipline: `minScore` here is on the **0.0-10.0 display scale**, exactly
 * as the user typed it, because that is what `postListQuerySchema.minScore`
 * declares. The server converts to 0-100 in one place. The dashboard never
 * multiplies or divides a score itself.
 *
 * Identifier discipline: every action here takes a `PostMatchId` (see
 * `lib/post-identity.ts`). The endpoints address `post_matches.id`; passing the
 * global `posts.id` used to compile fine and 404 at runtime.
 */

export interface PostFilters {
  page: number;
  pageSize: number;
  projectId: string;
  /** '' = mọi kênh; ngược lại là một `Platform` ('FACEBOOK' | 'THREADS'). */
  platform: string;
  keywordId: string;
  /** 0.0-10.0, the same scale shown in the list. Empty string means no filter. */
  minScore: string;
  from: string;
  to: string;
  sourceType: PostSourceType[];
  hasComment: boolean | null;
  hasPurchaseIntent: boolean | null;
  /** AI trả lời bài này có đúng ý định tìm kiếm của dự án không. null = không lọc. */
  matchesSearchIntent: boolean | null;
  /**
   * No `hasError` here on purpose. The list item's `hasError` is a constant
   * `false` server-side — nothing records a per-post error yet — so offering the
   * filter would take the user's choice and return an unfiltered list.
   */
  reviewStatus: PostReviewStatus[];
  seen: boolean | null;
  intent: PostIntent[];
  search: string;
  sortBy: PostSortField;
  sortOrder: SortOrder;
}

export function createDefaultFilters(): PostFilters {
  return {
    page: 1,
    pageSize: 25,
    projectId: '',
    platform: '',
    keywordId: '',
    minScore: '',
    from: '',
    to: '',
    sourceType: [],
    hasComment: null,
    hasPurchaseIntent: null,
    matchesSearchIntent: null,
    reviewStatus: [],
    seen: null,
    intent: [],
    search: '',
    sortBy: 'finalScore',
    sortOrder: 'desc',
  };
}

export const usePostsStore = defineStore('posts', () => {
  const items = ref<PostListItem[]>([]);
  const meta = ref<PaginationMeta | null>(null);
  const loading = ref(false);
  const error = ref<ApiRequestError | null>(null);
  const filters = ref<PostFilters>(createDefaultFilters());

  const detail = ref<PostDetailResponse | null>(null);
  const detailLoading = ref(false);
  const detailError = ref<ApiRequestError | null>(null);
  const detailOpen = ref(false);

  const actionInFlight = ref<Record<string, boolean>>({});
  /**
   * Posts that arrived over the socket while the current page was displayed.
   * The event carries identifiers only, not a full row, so the UI offers a
   * refresh instead of splicing an incomplete record into the table.
   */
  const pendingIngestCount = ref(0);
  const lastOpenCommand = ref<OpenPostResponse | null>(null);

  function queryParams(): Record<string, unknown> {
    const value = filters.value;
    return {
      page: value.page,
      pageSize: value.pageSize,
      projectId: value.projectId || undefined,
      platform: value.platform || undefined,
      keywordId: value.keywordId || undefined,
      minScore: value.minScore === '' ? undefined : Number(value.minScore),
      from: value.from || undefined,
      to: value.to || undefined,
      sourceType: value.sourceType.length > 0 ? value.sourceType : undefined,
      hasComment: value.hasComment ?? undefined,
      hasPurchaseIntent: value.hasPurchaseIntent ?? undefined,
      matchesSearchIntent: value.matchesSearchIntent ?? undefined,
      reviewStatus: value.reviewStatus.length > 0 ? value.reviewStatus : undefined,
      seen: value.seen ?? undefined,
      intent: value.intent.length > 0 ? value.intent : undefined,
      search: value.search || undefined,
      sortBy: value.sortBy,
      sortOrder: value.sortOrder,
    };
  }

  async function fetchList(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const response = await apiGet('/posts', postListResponseSchema, queryParams());
      items.value = response.items;
      meta.value = response.meta;
      pendingIngestCount.value = 0;
    } catch (caught) {
      error.value = toApiError(caught);
      items.value = [];
      meta.value = null;
    } finally {
      loading.value = false;
    }
  }

  function resetFilters(): void {
    filters.value = createDefaultFilters();
  }

  /**
   * The match id already carries the project — it *is* the project-scoped row —
   * so no `projectId` is sent. `GET /posts/:postMatchId` declares no query
   * parameters at all; the one this used to append was silently discarded.
   */
  async function openDetail(postMatchId: PostMatchId): Promise<void> {
    detailOpen.value = true;
    detailLoading.value = true;
    detailError.value = null;
    try {
      detail.value = await apiGet(`/posts/${postMatchId}`, postDetailResponseSchema);
    } catch (caught) {
      detailError.value = toApiError(caught);
      detail.value = null;
    } finally {
      detailLoading.value = false;
    }
  }

  function closeDetail(): void {
    detailOpen.value = false;
    detail.value = null;
    detailError.value = null;
  }

  async function review(postMatchId: PostMatchId, payload: PostReviewRequest): Promise<void> {
    actionInFlight.value = { ...actionInFlight.value, [postMatchId]: true };
    try {
      const response = await apiPatch(
        `/posts/${postMatchId}/review`,
        postReviewResponseSchema,
        payload,
      );

      items.value = items.value.map((item) =>
        item.postMatchId === response.postMatchId
          ? {
              ...item,
              reviewStatus: response.reviewStatus,
              isSeen: response.isSeen,
              finalScore10: response.latestScore?.finalScore10 ?? item.finalScore10,
              finalScore100: response.latestScore?.finalScore100 ?? item.finalScore100,
              author: { ...item.author, isBlocked: response.authorBlocked },
              source: { ...item.source, isBlocked: response.sourceBlocked },
            }
          : item,
      );

      if (detail.value && detail.value.match.id === response.postMatchId) {
        const latest = response.latestScore;
        detail.value = {
          ...detail.value,
          match: {
            ...detail.value.match,
            reviewStatus: response.reviewStatus,
            isSeen: response.isSeen,
          },
          latestScore: latest ?? detail.value.latestScore,
          // A manual override is appended as a new revision, never an edit of an
          // existing one, so the history grows rather than mutates.
          scoreHistory: latest
            ? [latest, ...detail.value.scoreHistory.filter((score) => score.id !== latest.id)]
            : detail.value.scoreHistory,
        };
      }
    } finally {
      const next = { ...actionInFlight.value };
      delete next[postMatchId];
      actionInFlight.value = next;
    }
  }

  async function openInBrowser(
    postMatchId: PostMatchId,
    body: { projectId?: string; browserProfileId?: string; bringToFront?: boolean },
  ): Promise<OpenPostResponse> {
    actionInFlight.value = { ...actionInFlight.value, [postMatchId]: true };
    try {
      const response = await apiPost(`/posts/${postMatchId}/open`, openPostResponseSchema, body);
      lastOpenCommand.value = response;
      return response;
    } finally {
      const next = { ...actionInFlight.value };
      delete next[postMatchId];
      actionInFlight.value = next;
    }
  }

  /**
   * Delete one match. The row leaves the local list immediately — a deleted row
   * that lingers until the next refetch reads as "the delete did nothing" — and
   * the drawer closes if it was showing exactly this match.
   */
  async function remove(postMatchId: PostMatchId): Promise<void> {
    actionInFlight.value = { ...actionInFlight.value, [postMatchId]: true };
    try {
      await apiDelete(`/posts/${postMatchId}`, deletePostResponseSchema);
      items.value = items.value.filter((item) => item.postMatchId !== postMatchId);
      if (meta.value) {
        meta.value = { ...meta.value, total: Math.max(0, meta.value.total - 1) };
      }
      if (detail.value?.match.id === postMatchId) {
        closeDetail();
      }
    } finally {
      const next = { ...actionInFlight.value };
      delete next[postMatchId];
      actionInFlight.value = next;
    }
  }

  /**
   * Delete every post matching the CURRENT filters, within the selected project.
   *
   * The body is the same filter set `fetchList` sends, minus pagination and
   * sorting, so what gets deleted is exactly what the table shows — not a
   * second reading of the filters that could disagree with the first. The
   * button is disabled without a project; this throw is the second lock, not
   * the user-facing one.
   */
  async function removeAllMatching(): Promise<BulkDeletePostsResponse> {
    if (!filters.value.projectId) {
      throw new Error('Xóa hàng loạt luôn giới hạn trong một project — chọn project trước.');
    }
    const filterBody = queryParams();
    // Pagination and sorting are display concerns; the delete is about the set.
    delete filterBody.page;
    delete filterBody.pageSize;
    delete filterBody.sortBy;
    delete filterBody.sortOrder;

    const response = await apiPost('/posts/bulk-delete', bulkDeletePostsResponseSchema, filterBody);
    // Whatever page the user was on may no longer exist.
    filters.value.page = 1;
    await fetchList();
    return response;
  }

  /**
   * Busy state is keyed by the same id the request was issued under, so a row's
   * spinner cannot be looked up under the other identifier and stay dark.
   */
  function isActionInFlight(postMatchId: PostMatchId): boolean {
    return actionInFlight.value[postMatchId] === true;
  }

  /** Realtime: `post.ingested`. */
  function applyPostIngested(event: PostIngested): void {
    const projectFilter = filters.value.projectId;
    if (projectFilter && projectFilter !== event.projectId) return;

    const existing = items.value.find((item) => item.postMatchId === event.postMatchId);
    if (existing) {
      // A re-scored or refreshed post: patch the score in place. A null score
      // means "chưa chấm", so it must not overwrite a number we already have.
      items.value = items.value.map((item) =>
        item.postMatchId === event.postMatchId
          ? {
              ...item,
              finalScore10: event.finalScore10 ?? item.finalScore10,
              lastDiscoveredAt: event.discoveredAt,
            }
          : item,
      );
      return;
    }

    pendingIngestCount.value += 1;
  }

  function reset(): void {
    items.value = [];
    meta.value = null;
    error.value = null;
    filters.value = createDefaultFilters();
    detail.value = null;
    detailError.value = null;
    detailOpen.value = false;
    pendingIngestCount.value = 0;
    lastOpenCommand.value = null;
  }

  return {
    items,
    meta,
    loading,
    error,
    filters,
    detail,
    detailLoading,
    detailError,
    detailOpen,
    actionInFlight,
    isActionInFlight,
    pendingIngestCount,
    lastOpenCommand,
    fetchList,
    resetFilters,
    openDetail,
    closeDetail,
    review,
    openInBrowser,
    remove,
    removeAllMatching,
    applyPostIngested,
    reset,
  };
});
